import { useEffect, useId, useRef, useState } from "react";

export type SelectOption = { value: string; label: string; hint?: string };

export function Select({
  label,
  value,
  options,
  onChange,
  placeholder = "Выбрать",
  disabled,
}: {
  label?: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={`vk-select ${open ? "open" : ""}`} ref={rootRef}>
      {label ? <div className="vk-select-label">{label}</div> : null}
      <button
        type="button"
        className="vk-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="vk-select-value">
          {current ? (
            <>
              <strong>{current.label}</strong>
              {current.hint ? <em>{current.hint}</em> : null}
            </>
          ) : (
            <span className="vk-select-placeholder">{placeholder}</span>
          )}
        </span>
        <span className="vk-select-chevron" aria-hidden>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M3 5l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {open ? (
        <ul className="vk-select-menu" role="listbox" id={listId}>
          {options.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={o.value === value ? "active" : ""}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                <span>{o.label}</span>
                {o.hint ? <em>{o.hint}</em> : null}
                {o.value === value ? (
                  <span className="vk-select-check" aria-hidden>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2.5 6.2l2.4 2.4 4.6-5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                ) : null}
              </button>
            </li>
          ))}
          {!options.length ? (
            <li className="vk-select-empty">Нет вариантов</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
